import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, ArrowRight, Sparkles } from "lucide-react";
import { getSessionId } from "../lib/analytics";
import MoodBoardModal from "./MoodBoardModal";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api`;
const IS_DEV = process.env.NODE_ENV !== "production";
const CONV_KEY = "sws_chat_conversation_id";

const WELCOME = "Hi! I'm Ayaan, an AI assistant for Shobha with Shubhra. Ask me about our services, locations we serve, or booking timelines — and I'll pass anything specific straight to Shubhra.";

function newId() {
  return (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `m-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function loadConvId() {
  try { return localStorage.getItem(CONV_KEY); } catch (e) {
    if (IS_DEV) console.warn("[chat] loadConvId failed", e);
    return null;
  }
}
function saveConvId(id) {
  try { localStorage.setItem(CONV_KEY, id); } catch (e) {
    if (IS_DEV) console.warn("[chat] saveConvId failed", e);
  }
}

async function streamChat({ conversationId, sessionId, message, onDelta, onDone, onError }) {
  const controller = new AbortController();
  try {
    const res = await fetch(`${API}/chat/message`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify({ conversation_id: conversationId || undefined, session_id: sessionId || undefined, message }),
      signal: controller.signal,
    });
    if (!res.ok || !res.body) {
      onError(new Error(`Chat error ${res.status}`));
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let currentEvent = "message";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";
      for (const part of parts) {
        const lines = part.split("\n");
        let ev = "message";
        let dataStr = "";
        for (const line of lines) {
          if (line.startsWith("event:")) ev = line.slice(6).trim();
          else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
        }
        currentEvent = ev || currentEvent;
        if (!dataStr) continue;
        try {
          const data = JSON.parse(dataStr);
          if (currentEvent === "start" && data.conversation_id) {
            saveConvId(data.conversation_id);
            onDelta(null, { conversationId: data.conversation_id });
          } else if (currentEvent === "error") {
            onError(new Error(data.error || "chat error"));
          } else if (currentEvent === "done") {
            onDone(data);
          } else if (data.delta) {
            onDelta(data.delta);
          }
        } catch (parseErr) {
          if (IS_DEV) console.warn("[chat] SSE parse", parseErr, dataStr);
        }
      }
    }
  } catch (err) {
    onError(err);
  }
}

function EscalationForm({ conversationId, onDone, onCancel }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API}/chat/escalate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: conversationId, ...form }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Failed to send");
      }
      onDone();
    } catch (err) {
      setError(err.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-4 border-t border-[hsl(var(--line))] bg-[hsl(var(--bg-alt))] space-y-3">
      <div className="kicker">Send your details to Shubhra</div>
      <input required placeholder="Your name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="w-full bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none py-2 text-sm" />
      <input required type="email" placeholder="Email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="w-full bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none py-2 text-sm" />
      <input placeholder="Phone (optional)" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className="w-full bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none py-2 text-sm" />
      <textarea placeholder="Anything else you'd like to share?" rows={2} value={form.note} onChange={(e)=>setForm({...form,note:e.target.value})} className="w-full bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none py-2 text-sm resize-none" />
      {error && <div className="text-xs text-[hsl(var(--accent))]">{error}</div>}
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={sending} className="flex-1 border border-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] transition-colors duration-500 px-4 py-2 text-[10px] tracking-[0.3em] uppercase disabled:opacity-60">
          {sending ? "Sending…" : "Send"}
        </button>
        <button type="button" onClick={onCancel} className="text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--ink-soft))]">Cancel</button>
      </div>
    </form>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ _id: newId(), role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState(() => loadConvId());
  const [showEscalate, setShowEscalate] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [moodOpen, setMoodOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, showEscalate]);

  // Allow any page to open the AI Mood Board via a global event.
  useEffect(() => {
    const handler = () => setMoodOpen(true);
    window.addEventListener("sws:open-mood-board", handler);
    return () => window.removeEventListener("sws:open-mood-board", handler);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { _id: newId(), role: "user", content: text }, { _id: newId(), role: "assistant", content: "", streaming: true }]);
    setBusy(true);

    let assistantIdx = -1;
    setMessages((m) => {
      assistantIdx = m.length - 1;
      return m;
    });

    await streamChat({
      conversationId,
      sessionId: getSessionId(),
      message: text,
      onDelta: (delta, meta) => {
        if (meta?.conversationId) {
          setConversationId(meta.conversationId);
        }
        if (delta) {
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last && last.role === "assistant") {
              copy[copy.length - 1] = { ...last, content: (last.content || "") + delta };
            }
            return copy;
          });
        }
      },
      onDone: () => {
        setBusy(false);
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last?.role === "assistant") {
            copy[copy.length - 1] = { ...last, streaming: false };
          }
          return copy;
        });
      },
      onError: (err) => {
        setBusy(false);
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last?.role === "assistant" && !last.content) {
            copy[copy.length - 1] = { _id: last._id || newId(), role: "assistant", content: `Sorry — I couldn't reach the studio just now. Please try again in a moment, or use the contact form.` };
          }
          return copy;
        });
        if (IS_DEV) console.warn("[chat] error", err);
      },
    });
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] shadow-lg hover:bg-[hsl(var(--accent))] transition-colors duration-500 flex items-center justify-center"
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? <X size={20} /> : <MessageCircle size={22} />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-[min(380px,calc(100vw-3rem))] max-h-[min(600px,calc(100vh-8rem))] bg-[hsl(var(--bg))] border border-[hsl(var(--line))] shadow-2xl flex flex-col">
          <div className="p-4 border-b border-[hsl(var(--line))] bg-[hsl(var(--ink))] text-[hsl(var(--bg))]">
            <div className="font-editorial text-lg tracking-wider leading-tight">Ayaan</div>
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">AI Assistant · Shobha with Shubhra</div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={m._id || `msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed rounded ${m.role === "user" ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]" : "bg-[hsl(var(--bg-alt))] text-[hsl(var(--ink))]"}`}>
                  {m.content || (m.streaming ? <Loader2 size={12} className="animate-spin inline" /> : "…")}
                </div>
              </div>
            ))}
          </div>

          {(() => {
            if (escalated) {
              return (
                <div className="p-4 border-t border-[hsl(var(--line))] text-sm text-center bg-[hsl(var(--bg-alt))]">
                  Thank you — Shubhra will reach out shortly.
                </div>
              );
            }
            if (showEscalate) {
              return (
                <EscalationForm
                  conversationId={conversationId}
                  onDone={() => { setShowEscalate(false); setEscalated(true); setMessages((m)=>[...m, { _id: newId(), role: "assistant", content: "Wonderful — I've passed your details along. Shubhra will personally reach out within 48 hours." }]); }}
                  onCancel={() => setShowEscalate(false)}
                />
              );
            }
            return (
              <>
                <div className="px-4 py-2 border-t border-[hsl(var(--line))] flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => setMoodOpen(true)}
                    data-testid="chat-mood-board-btn"
                    className="text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2 hover:text-[hsl(var(--accent))]"
                  >
                    <Sparkles size={12} /> Vision Studio
                  </button>
                  <button
                    onClick={() => setShowEscalate(true)}
                    disabled={!conversationId}
                    className="text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2 hover:text-[hsl(var(--accent))] disabled:opacity-40"
                  >
                    Book consultation <ArrowRight size={12} />
                  </button>
                </div>
                <div className="p-3 border-t border-[hsl(var(--line))] flex items-end gap-2">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKey}
                    rows={1}
                    placeholder="Ask about services, locations, timing…"
                    className="flex-1 bg-transparent outline-none text-sm resize-none max-h-24"
                  />
                  <button
                    onClick={send}
                    disabled={busy || !input.trim()}
                    className="h-9 w-9 flex items-center justify-center bg-[hsl(var(--ink))] text-[hsl(var(--bg))] hover:bg-[hsl(var(--accent))] transition-colors duration-500 disabled:opacity-40"
                    aria-label="Send"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      )}
      <MoodBoardModal open={moodOpen} onClose={() => setMoodOpen(false)} />
    </>
  );
}
